import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button, Spicy } from '@crave/ui';
import { ApiError } from '@crave/api-client';
import { api } from '../api.ts';

interface QueueItem {
  name: string;
  quantity: number;
  notes: string | null;
  modifiers: string[];
  allergens: string[];
  /** The pizza itself, or an add-on that made this one spicy (#65). */
  spicy?: boolean;
}
interface QueueOrder {
  id: string;
  order_number: string;
  status: string;
  version: number;
  units: number;
  position: number;
  is_up_next: boolean;
  pickup_label: string;
  start_prep_label: string;
  lateness_minutes: number;
  customer_name: string | null;
  notes: string | null;
  items: QueueItem[];
  next: { to: string; label: string } | null;
}
interface QueueResponse {
  location: { slug: string; name: string };
  up_next: QueueOrder | null;
  queue: QueueOrder[];
  behind_minutes: number;
  generated_at: string;
}

/** An order that was tapped PICKED UP but came back in another status (payment capture failed). */
interface PaymentAlert {
  id: string;
  orderNumber: string;
  customerName: string | null;
}

// Kept in localStorage so a tablet reload on flaky Wi-Fi does not wipe an unread payment alert.
// Only the order id and number are saved: the customer name stays in memory so it is never left on a
// shared kitchen tablet. The order number is enough for a manager to find the order.
const ALERTS_KEY = 'crave.kitchen.payment-alerts';

function loadAlerts(): PaymentAlert[] {
  try {
    const raw = localStorage.getItem(ALERTS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const alerts: PaymentAlert[] = [];
    for (const a of parsed as unknown[]) {
      if (typeof a !== 'object' || a === null) continue;
      const r = a as Record<string, unknown>;
      if (typeof r['id'] !== 'string' || typeof r['orderNumber'] !== 'string') continue;
      alerts.push({
        id: r['id'],
        orderNumber: r['orderNumber'],
        // Saved alerts never carry a name (older saves that did are ignored and rewritten).
        customerName: null,
      });
    }
    return alerts;
  } catch {
    return [];
  }
}

function saveAlerts(alerts: PaymentAlert[]) {
  try {
    if (alerts.length === 0) localStorage.removeItem(ALERTS_KEY);
    else
      localStorage.setItem(
        ALERTS_KEY,
        JSON.stringify(alerts.map(({ id, orderNumber }) => ({ id, orderNumber }))),
      );
  } catch {
    // Storage blocked: the alert still shows for this session.
  }
}

/** Forget saved payment alerts. Called on sign-out so the next person on the tablet starts clean. */
export function clearPaymentAlerts() {
  try {
    localStorage.removeItem(ALERTS_KEY);
  } catch {
    // Storage blocked: nothing was saved.
  }
}

function isAuthError(err: unknown): err is ApiError {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

function timeLabel(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const STAGE_LABEL: Record<string, string> = {
  QUEUED: 'Up next',
  PREPARING: 'Prepping',
  IN_OVEN: 'In the oven',
  BOXING: 'Boxing',
  READY: 'Ready for pickup',
  REMAKE: 'Remake',
  CANCEL_REQUESTED: 'Cancel requested, see manager',
};

export function Queue({ location }: { location: string }) {
  const qc = useQueryClient();
  const etag = useRef<string | null>(null);
  // A different location's queue must never answer "not modified" from this one's tag.
  useEffect(() => {
    etag.current = null;
  }, [location]);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [alerts, setAlerts] = useState<PaymentAlert[]>(loadAlerts);
  useEffect(() => saveAlerts(alerts), [alerts]);
  useEffect(() => {
    const on = () => setOffline(false),
      off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const nav = navigator as Navigator & {
      wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> };
    };
    nav.wakeLock
      ?.request('screen')
      .then((l) => {
        lock = l;
      })
      .catch(() => {});
    return () => {
      void lock?.release();
    };
  }, []);

  const q = useQuery({
    queryKey: ['queue', location],
    queryFn: async () => {
      const r = await api.get<QueueResponse>(
        `/staff/kitchen/queue?location=${encodeURIComponent(location)}`,
        etag.current ? { etag: etag.current } : undefined,
      );
      if (r.notModified) return qc.getQueryData<QueueResponse>(['queue', location])!;
      etag.current = r.etag;
      return r.data;
    },
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  });

  const advance = useMutation({
    mutationFn: async (o: QueueOrder) =>
      (
        await api.post<{ status: string; version: number }>(`/staff/orders/${o.id}/transition`, {
          to: o.next!.to,
          expected_version: o.version,
        })
      ).data,
    onSuccess: (data, o) => {
      // The API answers 200 even when capture fails: the order lands in MANUAL_REVIEW and drops off
      // the kitchen queue. Without this the card just vanishes as if the pickup went through.
      if (
        o.next?.to === 'PICKED_UP' &&
        data.status !== 'PICKED_UP' &&
        data.status !== 'COMPLETED'
      ) {
        setAlerts((prev) =>
          prev.some((a) => a.id === o.id)
            ? prev
            : [...prev, { id: o.id, orderNumber: o.order_number, customerName: o.customer_name }],
        );
      }
    },
    onSettled: () => {
      etag.current = null;
      void qc.invalidateQueries({ queryKey: ['queue'] });
    },
  });

  const dismissAlert = (id: string) => setAlerts((prev) => prev.filter((a) => a.id !== id));
  const alertBanners = alerts.map((a) => (
    <PaymentAlertBanner key={a.id} alert={a} onDismiss={() => dismissAlert(a.id)} />
  ));

  // Full screen only when there is nothing to show, or the login no longer works.
  // A failed poll with a queue already on screen keeps the tickets up (see the banner below).
  if (isAuthError(q.error) || (q.isError && !q.data))
    return (
      <Screen>
        {alertBanners}
        <p style={{ color: 'var(--crave-red)', fontSize: 22 }}>
          {isAuthError(q.error)
            ? q.error.message
            : q.error instanceof ApiError && q.error.status >= 400 && q.error.status < 500
              ? q.error.message
              : 'Cannot reach the kitchen queue. Check the connection.'}
        </p>
      </Screen>
    );
  if (!q.data)
    return (
      <Screen>
        {alertBanners}
        <p style={{ fontSize: 22 }}>Loading the queue…</p>
      </Screen>
    );
  const { up_next, queue, behind_minutes } = q.data;
  const working = queue.filter((o) =>
    ['PREPARING', 'IN_OVEN', 'BOXING', 'REMAKE'].includes(o.status),
  );
  const rest = queue.filter((o) => !working.includes(o) && o.id !== up_next?.id);
  const lastUpdate = timeLabel(q.dataUpdatedAt);
  const staleNote =
    q.error instanceof ApiError && q.error.status >= 400 && q.error.status < 500
      ? `${q.error.message.replace(/\.$/, '')}. Showing the last update from ${lastUpdate}.`
      : `Connection trouble, showing the last update from ${lastUpdate}.`;

  return (
    <Screen>
      {alertBanners}
      {offline && (
        <Banner color="var(--crave-red)">
          Offline. Buttons are disabled until the connection returns.
        </Banner>
      )}
      {q.isError && !offline && (
        <Banner color="var(--warn)" small>
          {staleNote}
        </Banner>
      )}
      {behind_minutes > 0 && (
        <Banner color="var(--warn)">Running about {behind_minutes} min behind.</Banner>
      )}
      {advance.error && (
        <Banner color="var(--crave-red)">
          {advance.error instanceof ApiError
            ? advance.error.message
            : 'That tap did not go through.'}
        </Banner>
      )}
      {working.map((o) => (
        <OrderCard
          key={o.id}
          order={o}
          big
          onAdvance={() => advance.mutate(o)}
          busy={advance.isPending}
          disabled={offline}
        />
      ))}
      {up_next && (
        <OrderCard
          order={up_next}
          big
          onAdvance={() => advance.mutate(up_next)}
          busy={advance.isPending}
          disabled={offline}
        />
      )}
      {!up_next && working.length === 0 && (
        <p style={{ fontSize: 28, textAlign: 'center', color: 'var(--ink-soft)', padding: 40 }}>
          Nothing in the queue. 🍕
        </p>
      )}
      {rest.length > 0 && (
        <div style={{ display: 'grid', gap: 12, opacity: 0.9 }}>
          <h2 style={{ margin: '8px 0 0', fontSize: 18, color: 'var(--ink-soft)' }}>Then</h2>
          {rest.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              onAdvance={() => advance.mutate(o)}
              busy={advance.isPending}
              disabled={offline}
            />
          ))}
        </div>
      )}
    </Screen>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <main style={{ maxWidth: 720, margin: '0 auto', padding: 16, display: 'grid', gap: 16 }}>
      {children}
    </main>
  );
}
function Banner({
  color,
  small,
  children,
}: {
  color: string;
  small?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      role="status"
      style={{
        background: color,
        color: '#fff',
        padding: small ? '6px 14px' : '10px 16px',
        borderRadius: 10,
        fontWeight: small ? 600 : 700,
        fontSize: small ? 15 : undefined,
      }}
    >
      {children}
    </div>
  );
}

function PaymentAlertBanner({ alert, onDismiss }: { alert: PaymentAlert; onDismiss: () => void }) {
  return (
    <div
      role="alert"
      style={{
        background: 'var(--crave-red)',
        color: '#fff',
        padding: 20,
        borderRadius: 'var(--radius)',
        border: '3px solid var(--ink)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 300px', display: 'grid', gap: 4 }}>
        <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>
          Payment did not go through for #{alert.orderNumber.slice(-2)}
        </div>
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          Order {alert.orderNumber}
          {alert.customerName ? ` for ${alert.customerName}` : ''}. Get a manager before handing it
          over.
        </div>
      </div>
      <Button size="lg" variant="secondary" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}

function OrderCard({
  order,
  big,
  onAdvance,
  busy,
  disabled,
}: {
  order: QueueOrder;
  big?: boolean;
  onAdvance: () => void;
  busy: boolean;
  disabled: boolean;
}) {
  const late = order.lateness_minutes > 0;
  return (
    <section
      style={{
        background: 'var(--paper)',
        border: `3px solid ${big ? 'var(--ink)' : 'var(--line)'}`,
        borderRadius: 'var(--radius)',
        padding: big ? 24 : 16,
        display: 'grid',
        gap: big ? 16 : 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--crave-red)',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
            }}
          >
            {order.is_up_next ? 'UP NEXT' : (STAGE_LABEL[order.status] ?? order.status)}
          </div>
          <div style={{ fontSize: big ? 40 : 24, fontWeight: 800, lineHeight: 1.1 }}>
            #{order.order_number.slice(-2)}{' '}
            <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink-soft)' }}>
              {order.order_number}
            </span>
          </div>
        </div>
        <style>{`@media (max-width: 560px) { .kq-pickup { text-align: left !important; } }`}</style>
        <div className="kq-pickup" style={{ textAlign: 'right' }}>
          <div style={{ fontSize: big ? 28 : 18, fontWeight: 700 }}>
            Pickup {order.pickup_label}
          </div>
          {order.status === 'QUEUED' && (
            <div style={{ fontSize: 14, color: late ? 'var(--crave-red)' : 'var(--ink-soft)' }}>
              {late ? `${order.lateness_minutes} min late` : `start by ${order.start_prep_label}`}
            </div>
          )}
        </div>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
        {order.items.map((i, idx) => (
          <li key={idx} style={{ fontSize: big ? 26 : 18, fontWeight: 600 }}>
            {i.quantity} × {i.name}
            {i.spicy && (
              <>
                {' '}
                <Spicy size={big ? 'md' : 'sm'} />
              </>
            )}
            {i.modifiers.length > 0 && (
              <div style={{ fontSize: big ? 20 : 15, fontWeight: 500, color: 'var(--crave-red)' }}>
                {i.modifiers.map((m) => `- ${m}`).join('  ')}
              </div>
            )}
            {i.notes && (
              <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink-soft)' }}>
                “{i.notes}”
              </div>
            )}
          </li>
        ))}
      </ul>
      {order.customer_name && <div style={{ color: 'var(--ink-soft)' }}>{order.customer_name}</div>}
      {order.next && (
        <Button size={big ? 'xl' : 'lg'} onClick={onAdvance} disabled={busy || disabled}>
          {order.next.label}
        </Button>
      )}
    </section>
  );
}
